import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import { FiX, FiArrowUp, FiArrowDown, FiMenu } from 'react-icons/fi';
import { fetchAllTutorials, reorderTutorial, selectTutorials, selectLoading } from '../../../../../app/store/slices/adminSlice';
import { toast } from 'react-toastify';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


const SortableItem = ({ tutorial, index, moveUp, moveDown, totalItems }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tutorial._id || tutorial.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-white border rounded-lg ${
        isDragging ? 'shadow-lg border-btn-primary' : 'border-gray-200'
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing p-2 hover:bg-gray-100 rounded"
      >
        <FiMenu className="text-gray-400" />
      </div>
      <div className="flex-shrink-0 w-12 text-center font-bold text-gray-600">
        {index + 1}
      </div>
      <div className="flex-1">
        <div className="font-medium text-gray-900">{tutorial.title}</div>
        <div className="text-xs text-gray-500">
          {tutorial.category} • {tutorial.level}
        </div>
      </div>
      <div className="flex gap-1">
        <button
          onClick={() => moveUp(index)}
          disabled={index === 0}
          className="p-2 text-gray-600 hover:text-btn-primary disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <FiArrowUp />
        </button>
        <button
          onClick={() => moveDown(index)}
          disabled={index === totalItems - 1}
          className="p-2 text-gray-600 hover:text-btn-primary disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <FiArrowDown />
        </button>
      </div>
    </div>
  );
};

const TutorialReorderModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { tutorials } = useSelector(selectTutorials) || { tutorials: [] };
  const loading = useSelector(selectLoading);
  const [orderedTutorials, setOrderedTutorials] = useState([]);
  const [originalTutorials, setOriginalTutorials] = useState([]);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (open) {
      dispatch(fetchAllTutorials({ 
        page: 1, 
        limit: 1000, 
        sortBy: 'sequence', 
        sortOrder: 'asc' 
      }));
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (tutorials.length > 0) {
      const sorted = [...tutorials].sort((a, b) => a.sequence - b.sequence);
      setOrderedTutorials(sorted);
      setOriginalTutorials(sorted);
    }
  }, [tutorials]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedTutorials((items) => {
        const oldIndex = items.findIndex((item) => (item._id || item.id) === active.id);
        const newIndex = items.findIndex((item) => (item._id || item.id) === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const moveUp = (index) => {
    if (index === 0) return;
    setOrderedTutorials((items) => arrayMove(items, index, index - 1));
  };

  const moveDown = (index) => {
    if (index === orderedTutorials.length - 1) return;
    setOrderedTutorials((items) => arrayMove(items, index, index + 1));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const tutorialsToUpdate = [];
      orderedTutorials.forEach((tutorial, index) => {
        const newSequence = index + 1;
        const originalIndex = originalTutorials.findIndex(
          t => (t._id || t.id) === (tutorial._id || tutorial.id)
        );
        if (originalIndex !== -1 && originalTutorials[originalIndex].sequence !== newSequence) {
          tutorialsToUpdate.push({
            id: tutorial._id || tutorial.id,
            newSequence,
            oldSequence: originalTutorials[originalIndex].sequence
          });
        }
      });

      if (tutorialsToUpdate.length === 0) {
        toast.info('No changes to save');
        onClose();
        return;
      }
      tutorialsToUpdate.sort((a, b) => {
        if (a.newSequence > a.oldSequence && b.newSequence <= b.oldSequence) return -1;
        if (a.newSequence <= a.oldSequence && b.newSequence > b.oldSequence) return 1;
        return b.newSequence - a.newSequence;
      });

      for (let i = 0; i < tutorialsToUpdate.length; i++) {
        const tutorial = tutorialsToUpdate[i];
        try {
          await dispatch(reorderTutorial({ 
            id: tutorial.id, 
            newSequence: tutorial.newSequence 
          })).unwrap();

          if (i < tutorialsToUpdate.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        } catch (err) {
          console.warn(`Retry reordering tutorial ${tutorial.id} to sequence ${tutorial.newSequence}`);
          await new Promise(resolve => setTimeout(resolve, 300));
          await dispatch(reorderTutorial({ 
            id: tutorial.id, 
            newSequence: tutorial.newSequence 
          })).unwrap();
        }
      }

      await dispatch(fetchAllTutorials({ 
        page: 1, 
        limit: 1000, 
        sortBy: 'sequence', 
        sortOrder: 'asc' 
      }));
      
      toast.success('Tutorials reordered successfully');
      onSuccess();
    } catch (error) {
      toast.error(`Failed to reorder tutorials: ${error.message || error}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex justify-between items-center border-b pb-4">
        <span className="text-xl font-bold text-btn-primary">Reorder Tutorials</span>
        <button
          onClick={onClose}
          disabled={saving}
          className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <FiX size={24} />
        </button>
      </DialogTitle>

      <DialogContent className="mt-4">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <CircularProgress />
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-600 mb-4">
              Drag and drop tutorials to reorder them, or use the arrow buttons.
            </p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedTutorials.map(t => t._id || t.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {orderedTutorials.map((tutorial, index) => (
                    <SortableItem
                      key={tutorial._id || tutorial.id}
                      tutorial={tutorial}
                      index={index}
                      moveUp={moveUp}
                      moveDown={moveDown}
                      totalItems={orderedTutorials.length}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </DialogContent>

      <DialogActions className="border-t px-6 py-4">
        <button
          onClick={onClose}
          disabled={saving}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || loading}
          className="px-4 py-2 bg-btn-primary text-btn-primary-text rounded-lg hover:bg-btn-primary-hover disabled:opacity-50 flex items-center gap-2"
        >
          {saving && <CircularProgress size={16} color="inherit" />}
          Save Order
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialReorderModal;